import { motion } from 'framer-motion';
import styled from 'styled-components';
import { FaRocket, FaUsers, FaChartLine, FaClock } from 'react-icons/fa';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';

const StatsSection = styled(motion.section)`
  position: relative;
  background: ${props => props.theme.colors.background};
  padding: 4rem 1.5rem;
  overflow: hidden;
`;

const Container = styled(motion.div)`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
`;

const StatsGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatCard = styled(motion.div)`
  text-align: center;
  padding: 2rem;
  border-radius: 8px;
  background: ${props => props.theme.colors.surface};
  box-shadow: 0 1px 3px ${props => props.theme.isDark ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)'};
  border: 1px solid ${props => props.theme.colors.border};
`;

const IconWrapper = styled(motion.div)`
  width: 48px;
  height: 48px;
  background: ${props => props.theme.isDark ? 
    `linear-gradient(135deg, ${props.theme.colors.primary}20, ${props.theme.colors.secondary}20)` : 
    '#f8fafc'
  };
  border-radius: ${props => props.theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.5rem;
  color: ${props => props.color || props.theme.colors.primary};
  transition: ${props => props.theme.transitions.default};

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: scale(1.2);
  }
`;

const Value = styled(motion.div)`
  font-size: 2rem;
  font-weight: 600;
  line-height: 1;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.text.primary};
`;

const Label = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.text.secondary};
  font-weight: 500;
`;

const Statistics = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const stats = [
    {
      icon: FaRocket,
      value: 50000,
      label: "Active Users",
      suffix: "+"
    },
    {
      icon: FaUsers,
      value: 95,
      label: "Client Satisfaction",
      suffix: "%"
    },
    {
      icon: FaChartLine,
      value: 24,
      label: "Monthly Growth",
      suffix: "%"
    },
    {
      icon: FaClock,
      value: 99.9,
      label: "System Uptime",
      suffix: "%"
    }
  ];

  return (
    <StatsSection>
      <Container>
        <StatsGrid ref={ref}>
          {stats.map((stat, index) => (
            <StatCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <IconWrapper>
                <stat.icon />
              </IconWrapper>
              <Value>
                {inView && (
                  <CountUp
                    end={stat.value}
                    duration={2}
                    decimals={stat.value % 1 !== 0 ? 1 : 0}
                    suffix={stat.suffix}
                  />
                )}
              </Value>
              <Label>{stat.label}</Label>
            </StatCard>
          ))}
        </StatsGrid>
      </Container>
    </StatsSection>
  );
};

export default Statistics;
